import { useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import type { AmazonExperimentMetrics } from "@/types/ab-tests";
import { METRIC_ROWS } from "./constants";

type MetricValue =
  | { a: string; b: string; diff: string }
  | { a: number; b: number; diff: number }
  | undefined;

interface ResultsSectionProps {
  metrics: AmazonExperimentMetrics;
  onMetricsChange: (metrics: AmazonExperimentMetrics) => void;
}

function parseCsvToMetrics(text: string): AmazonExperimentMetrics {
  const labelToKey: Record<string, keyof AmazonExperimentMetrics> = {
    conversion: "conversion",
    "units per unique visitor": "unitsPerUniqueVisitor",
    "units / unique visitor": "unitsPerUniqueVisitor",
    "units sold": "unitsSold",
    "units sold from search": "unitsSoldFromSearch",
    sales: "sales",
    "sales from search": "salesFromSearch",
    "sample size": "sampleSize",
  };
  const numericKeys = new Set(["unitsPerUniqueVisitor", "unitsSold", "unitsSoldFromSearch", "sampleSize"]);
  const next: AmazonExperimentMetrics = {};
  const lines = text.split(/\r?\n/).filter(Boolean);
  for (const line of lines) {
    const cols = line.split(",").map((c) => c.trim());
    if (cols.length < 4) continue;
    const [labelRaw, a, b, diff] = cols;
    const key = labelToKey[labelRaw.toLowerCase()];
    if (!key) continue;
    if (numericKeys.has(key)) {
      (next as Record<string, unknown>)[key] = { a: Number(a) || 0, b: Number(b) || 0, diff: Number(diff) || 0 };
    } else {
      (next as Record<string, unknown>)[key] = { a, b, diff };
    }
  }
  return next;
}

function computeDiff(a: string | number, b: string | number, numeric?: boolean): string | number {
  if (numeric) {
    const an = typeof a === "number" ? a : Number(a);
    const bn = typeof b === "number" ? b : Number(b);
    if (Number.isNaN(an) || Number.isNaN(bn) || a === "" || b === "") return "";
    return bn - an;
  }
  const aStr = String(a ?? "");
  const bStr = String(b ?? "");
  if (!aStr || !bStr) return "";
  const isPct = aStr.includes("%") || bStr.includes("%");
  const an = parseFloat(aStr.replace(/[^0-9.-]/g, ""));
  const bn = parseFloat(bStr.replace(/[^0-9.-]/g, ""));
  if (Number.isNaN(an) || Number.isNaN(bn)) return "";
  const diff = bn - an;
  const sign = diff > 0 ? "+" : "";
  if (isPct) return `${sign}${diff.toFixed(1)}pp`;
  if (aStr.trim().startsWith("$") || bStr.trim().startsWith("$")) {
    return `${diff < 0 ? "-" : sign}$${Math.abs(diff).toLocaleString()}`;
  }
  return `${sign}${diff}`;
}

export function ResultsSection({ metrics, onMetricsChange }: ResultsSectionProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleCsvUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = parseCsvToMetrics(String(reader.result || ""));
        if (Object.keys(parsed).length === 0) {
          toast.error("No metrics found", { description: "Expected rows like: Conversion, 12.3%, 14.1%, +1.8pp" });
          return;
        }
        onMetricsChange(parsed);
        toast.success("Results uploaded", { description: `Loaded ${Object.keys(parsed).length} metrics from CSV.` });
      } catch {
        toast.error("Could not parse CSV");
      }
    };
    reader.readAsText(file);
  };

  const update = (key: keyof AmazonExperimentMetrics, field: "a" | "b", v: string, numeric?: boolean) => {
    onMetricsChange((() => {
      const existing = (metrics[key] as { a: string | number; b: string | number; diff: string | number } | undefined) ?? { a: "", b: "", diff: "" };
      const nextField = numeric ? (v === "" ? "" : Number(v)) : v;
      const merged = { ...existing, [field]: nextField };
      merged.diff = computeDiff(merged.a, merged.b, numeric);
      return { ...metrics, [key]: merged };
    })());
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 pb-4">
        <p className="text-xs text-muted-foreground">Edit metrics directly or re-upload a CSV to replace all values.</p>
        <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} className="gap-1.5">
          <Upload className="h-3.5 w-3.5" />Upload results
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleCsvUpload(file);
            e.target.value = "";
          }}
        />
      </div>
      <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
        <div>Metric</div>
        <div className="text-right">Variant A</div>
        <div className="text-right">Variant B</div>
        <div className="text-right">Difference</div>
      </div>
      {METRIC_ROWS.map((row) => {
        const raw = metrics[row.key] as MetricValue;
        const value = raw ?? { a: "", b: "", diff: "" };
        return (
          <div key={row.key} className="grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-2 items-center">
            <Label className="text-sm text-foreground">{row.label}</Label>
            <Input className="text-right" value={String(value.a ?? "")} onChange={(e) => update(row.key, "a", e.target.value, row.numeric)} />
            <Input className="text-right" value={String(value.b ?? "")} onChange={(e) => update(row.key, "b", e.target.value, row.numeric)} />
            <div className="text-right text-sm text-muted-foreground tabular-nums px-3 py-2 bg-muted rounded-md border border-input">
              {value.diff === "" || value.diff === undefined ? "—" : String(value.diff)}
            </div>
          </div>
        );
      })}
    </div>
  );
}
